import { ArrowRight, FolderPlus, Folder } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { OwnershipBadge } from "@/components/locations/OwnershipBadge";
import { sportTypeIcons } from "@/lib/sport-icons";
import type { OwnershipType } from "@/types/domain";

const SPORT_TYPE_LABELS: Record<string, string> = {
  soccer_pitch: "Soccer",
  tennis_court: "Tennis",
  padel_court: "Padel",
  basketball_court: "Basketball",
  volleyball_court: "Volleyball",
  running_track: "Running",
  baseball_diamond: "Baseball",
  rugby_pitch: "Rugby",
  field_hockey_pitch: "Hockey",
  cricket_pitch: "Cricket",
  skateboard_park: "Skate",
  multi_sport_court: "Multi-Sport",
  beach_volleyball_court: "Beach V-ball",
  petanque_court: "Petanque",
  street_workout_area: "Workout",
  pickleball_court: "Pickleball",
  ping_pong_table: "Ping Pong",
};

interface CollectionInfo {
  id: string;
  name: string;
  color: string;
}

interface MapTooltipContentProps {
  locationId: string;
  name: string;
  sportTypes: string[] | null;
  ownership: string;
  confidence: number | null;
  address: string | null;
  screenshotUrl: string | null;
  screenshotLoading: boolean;
  collections: CollectionInfo[] | null;
  onAddToCollection: (locationId: string) => void;
  onClose: () => void;
  onViewDetails: (locationId: string) => void;
}

export function MapTooltipContent({
  locationId,
  name,
  sportTypes,
  ownership,
  confidence,
  address,
  screenshotUrl,
  screenshotLoading,
  collections,
  onAddToCollection,
  onClose,
  onViewDetails,
}: MapTooltipContentProps) {
  return (
    <div className="w-[280px] bg-card text-card-foreground">
      {/* Screenshot banner */}
      <div className="h-[120px] w-full bg-muted relative overflow-hidden">
        {screenshotLoading ? (
          <Skeleton className="h-full w-full rounded-none" />
        ) : screenshotUrl ? (
          <img
            src={screenshotUrl}
            alt={`Satellite view of ${name}`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">
            No screenshot
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        {/* Name + confidence */}
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-tight line-clamp-2">{name}</p>
          {confidence != null && (
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap shrink-0">
              {Math.round(confidence * 100)}%
            </span>
          )}
        </div>

        {/* Sport type pills + ownership */}
        <div className="flex flex-wrap items-center gap-1">
          {sportTypes && sportTypes.length > 0 ? (
            sportTypes.slice(0, 3).map((type) => {
              const Icon = sportTypeIcons[type];
              return (
                <span
                  key={type}
                  className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                >
                  {Icon && <Icon size={12} stroke={1.5} />}
                  {SPORT_TYPE_LABELS[type] ?? type}
                </span>
              );
            })
          ) : null}
          {sportTypes && sportTypes.length > 3 && (
            <span className="text-[11px] text-muted-foreground">
              +{sportTypes.length - 3}
            </span>
          )}
          <OwnershipBadge
            ownership={ownership as OwnershipType}
            className="text-[11px] px-1.5 py-0"
          />
        </div>

        {/* Address */}
        {address && (
          <p className="text-xs text-muted-foreground line-clamp-1">{address}</p>
        )}

        {/* Collections */}
        {collections && collections.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            {collections.map((col) => (
              <span
                key={col.id}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                style={{ backgroundColor: col.color }}
              >
                <Folder className="h-2.5 w-2.5" />
                {col.name}
              </span>
            ))}
          </div>
        )}

        {/* Quick actions */}
        <div className="flex items-center gap-1.5 pt-0.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs flex-1"
            onClick={() => {
              onClose();
              onViewDetails(locationId);
            }}
          >
            View details
            <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onAddToCollection(locationId)}
          >
            <FolderPlus className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
